import { Button, Dialog, IconButton, Stack, styled, Typography } from '@mui/material';
import { DATE_FORMAT, SHARED } from '@schooly/constants';
import {
  ArrowRightIcon,
  BlogSvg,
  BookACallSvg,
  CopyAction,
  CrossIcon,
  LogoSvg,
  ModalContent,
  ModalHeader,
  TimeIcon,
} from '@schooly/style';
import { FC, PropsWithChildren, ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';
import { differenceInDays, format } from 'date-fns';
import { newDateTimezoneOffset } from '@schooly/utils/date';

const EMAIL = 'sales@schooly.org';
const BANNER_HEIGHT = 5.5;

export const TrialBannerLayout: FC<PropsWithChildren> = ({ children }) => {
  const { formatMessage } = useIntl();
  const { currentSchool } = useSchool();
  const [isTalkToSalesOpen, setIsTalkToSalesOpen] = useState(false);

  if (!currentSchool) return children;

  const trialEndDate = currentSchool?.trial_expiry_date
    ? newDateTimezoneOffset(currentSchool.trial_expiry_date)
    : undefined;

  if (!trialEndDate) {
    return children;
  }

  const daysLeft = differenceInDays(trialEndDate, new Date());

  const mailtoHref = `mailto:${EMAIL}?subject=${encodeURIComponent(
    formatMessage({ id: 'trial-banner-EmailSubject' }, { schoolName: currentSchool.name }),
  )}`;

  if (daysLeft > 0) {
    return (
      <>
        <TrialBanner
          label={formatMessage(
            { id: 'trial-banner-YouInTrialMode' },
            { daysLeft, trialEndDate: format(trialEndDate, DATE_FORMAT) },
          )}
          buttonLabel={formatMessage({ id: 'trial-banner-TalkToSales' })}
          onContactClick={() => setIsTalkToSalesOpen(true)}
        />
        <Stack
          sx={(theme) => ({
            height: `calc(100% - ${theme.spacing(BANNER_HEIGHT)})`,
            '--trial-banner-height': theme.spacing(BANNER_HEIGHT),
            overflow: 'auto',
          })}
        >
          {children}
        </Stack>
        <TrialContactDialog
          dataTestId={SHARED.TRIAL_CONTACT_DIALOG}
          open={isTalkToSalesOpen}
          onClose={() => setIsTalkToSalesOpen(false)}
          mailtoHref={mailtoHref}
          image={<BookACallSvg />}
          header={
            <Typography variant="h1">
              {formatMessage({ id: 'trial-banner-ContactTitle' })}
            </Typography>
          }
          description={
            <Typography variant="h3" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
              {formatMessage({ id: 'trial-banner-ContactDescription' })}
            </Typography>
          }
        />
      </>
    );
  }

  return (
    <>
      {children}
      <TrialContactDialog
        dataTestId={SHARED.TRIAL_EXPIRED_DIALOG}
        mailtoHref={mailtoHref}
        header={
          <Typography variant="h1" pt={1}>
            {formatMessage({ id: 'trial-banner-TrialEnded' })}
            <br />
            {formatMessage({ id: 'trial-banner-LovedWhatYouSaw' })}
          </Typography>
        }
        description={
          <Stack gap={3}>
            <Typography variant="h3" color="text.primary">
              {formatMessage({ id: 'trial-banner-TrialEndedDescription' })}
            </Typography>
            <Typography variant="h3" color="text.primary">
              {formatMessage({ id: 'trial-banner-TrialEndedEmailAssist' })}
            </Typography>
          </Stack>
        }
      />
    </>
  );
};

type TrialBannerContainerProps = { label: string; buttonLabel: string; onContactClick: () => void };

const TrialBanner: FC<TrialBannerContainerProps> = ({ label, buttonLabel, onContactClick }) => {
  return (
    <Stack
      data-test-id={SHARED.TRIAL_BANNER}
      sx={(theme) => ({
        position: 'relative',
        zIndex: theme.zIndex.appBar,
        height: theme.spacing(BANNER_HEIGHT),
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        gap: theme.spacing(2),
        backgroundColor: theme.palette.primary.main,
      })}
    >
      <Typography variant="h2" color="common.white">
        <TimeIcon />
      </Typography>
      <Typography variant="h3" color="common.white">
        {label}
      </Typography>
      <BannerButton
        onClick={onContactClick}
        variant="contained"
        size="small"
        endIcon={<ArrowRightIcon />}
      >
        {buttonLabel}
      </BannerButton>
    </Stack>
  );
};

const BannerButton = styled(Button)(({ theme }) => {
  return {
    ...theme.typography.body1,
    backgroundColor: 'white',
    color: theme.palette.primary.main,
    border: '1px solid white',
    transition: 'all .2s ease',

    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.text.disabled,
      borderColor: theme.palette.text.disabled,
    },
  };
});

type TrialContactDialogProps = {
  open?: boolean;
  onClose?: () => void;
  mailtoHref: string;
  image?: ReactNode;
  header: ReactNode;
  description: ReactNode;
  dataTestId?: string;
};

const TrialContactDialog: FC<TrialContactDialogProps> = ({
  open = true,
  onClose,
  mailtoHref,
  image = <BlogSvg />,
  header,
  description,
  dataTestId,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      data-test-id={dataTestId}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,
        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
    >
      <ModalHeader withBorderBottom={false} active title={<LogoSvg />}>
        {onClose && (
          <IconButton onClick={onClose}>
            <CrossIcon />
          </IconButton>
        )}
      </ModalHeader>
      <ModalContent active sx={{ pt: 0 }}>
        <Stack alignItems="center" mb={3}>
          {image}
        </Stack>
        <Stack gap={1}>
          {header}
          {description}
        </Stack>
        <Stack pt={2} gap={2} direction="row" justifyContent="flex-start">
          <Typography
            variant="h2"
            component="a"
            href={mailtoHref}
            sx={{ textDecoration: 'underline' }}
          >
            {EMAIL}
          </Typography>
          <CopyAction
            data-test-id={SHARED.TRIAL_CONTACT_EMAIL}
            text={
              <>
                <span className="copy-label">{formatMessage({ id: 'action-Copy' })}</span>
                <span className="copied-label">{formatMessage({ id: 'action-Copied' })}</span>
              </>
            }
            textVariant="h3"
            onCopy={() => navigator.clipboard.writeText(EMAIL)}
            sx={{
              '& .copied-label': { display: 'none' },
              '&.copied': {
                '& > *, &:hover > *, & .svg-icon': { color: 'success.main' },
                '& .copy-label': { display: 'none' },
                '& .copied-label': { display: 'inline' },
              },
            }}
          />
        </Stack>
      </ModalContent>
    </Dialog>
  );
};
